import React from 'react';
import Skeleton from './Skeleton';

/**
 * MD3 Data Table Component
 * Follows Material Design 3 guidelines for data tables:
 * - 52px rows, 16px vertical padding, 24px between columns
 * - Numeric/measurement columns right-aligned
 * - Icon-based sort indicators (only when sortable)
 * - Hover on surface-container-high, focus ring on clickable rows
 * - Skeleton loading states
 * - Staggered row animations
 */
export default function DataTable({
  columns,
  data,
  loading,
  rowKey,
  onRowClick,
  emptyMessage,
  sortConfig,
  onSort,
  actions
}) {
  const handleSort = (columnKey) => {
    if (onSort) onSort(columnKey);
  };

  const SortIndicator = ({ column }) => {
    const active = sortConfig?.key === column.key;
    const Icon = () => (
      <span className="md3-sort-icon" aria-hidden="true">
        <span className="material-symbols-rounded">
          {!active
            ? 'unfold_more'
            : sortConfig.direction === 'asc'
            ? 'arrow_upward'
            : 'arrow_downward'}
        </span>
      </span>
    );
    return <Icon />;
  };

  const headerAlign = (col) => {
    if (col.align === 'right' || col.numeric) return 'md3-table-header-right';
    if (col.align === 'center') return 'md3-table-header-center';
    return '';
  };

  const cellAlign = (col) => {
    if (col.align === 'right' || col.numeric) return 'md3-table-cell-right';
    if (col.align === 'center') return 'md3-table-cell-center';
    return '';
  };

  const colSpan = columns.length + (actions ? 1 : 0);

  return (
    <div className="md3-table-wrapper">
      <table className="md3-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={[
                  'md3-table-header',
                  sortConfig?.key === col.key ? 'md3-table-header-active' : '',
                  onSort ? 'md3-table-header-sortable' : '',
                  headerAlign(col)
                ]
                  .join(' ')
                  .trim()}
                onClick={() => onSort && handleSort(col.key)}
              >
                {col.title}
                {onSort && <SortIndicator column={col} />}
              </th>
            ))}
            {actions && <th className="md3-table-header md3-table-header-actions" scope="col" />}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={colSpan} className="md3-table-loading">
                <Skeleton variant="table-row" count={5} columns={columns.length} />
              </td>
            </tr>
          ) : !data?.length ? (
            <tr>
              <td colSpan={colSpan} className="md3-table-empty">
                {emptyMessage || 'No items'}
              </td>
            </tr>
          ) : (
            data.map((row, index) => (
              <tr
                key={row[rowKey]}
                className={`md3-table-row ${onRowClick ? 'md3-table-row-clickable' : ''}`}
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => onRowClick?.(row)}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={(e) => {
                  if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onRowClick(row);
                  }
                }}
              >
                {columns.map((col) => (
                  <td key={col.key} className={`md3-table-cell ${cellAlign(col)}`}>
                    {col.render ? col.render(row[col.key], row) : row[col.key]}
                  </td>
                ))}
                {actions && (
                  <td className="md3-table-cell md3-table-cell-actions">{actions(row)}</td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
